/**
 * @description Customer portal API client for token validation, invoices, payments, and estimates.
 *
 * @dependencies
 * - apiClient: Configured Axios client for the public portal API.
 *
 * @notes
 * - Invoice detail requests can present the active portal credential without placing it in a query string.
 * - Money values follow each endpoint's existing dollar/cents response contract.
 */

import apiClient from './apiClient';

const portalService = {
  // NEW: Validate token-based access
  validateToken: async (token) => {
    console.log('🔍 portalService.validateToken called with token');
    
    try {
      const response = await apiClient.get(`/portal/validate-token/${token}`);
      console.log('✅ Token validation successful:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Token validation failed:', error.response || error);
      throw error;
    }
  },
  
  // Resolve short URL code (new /p/:shortCode flow)
  resolveShortCode: async (shortCode) => {
    console.log('🔍 portalService.resolveShortCode called with:', shortCode);

    try {
      const response = await apiClient.get(`/portal/resolve/${shortCode}`);
      console.log('✅ Short code resolution successful:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Short code resolution failed:', error.response || error);
      throw error;
    }
  },

  // Validate portal access
  validatePortal: async (customerPublicId) => {
    console.log('🔍 portalService.validatePortal called with:', customerPublicId);
    console.log('🔍 API endpoint:', `/portal/validate/${customerPublicId}`);
    
    try {
      const response = await apiClient.get(`/portal/validate/${customerPublicId}`);
      console.log('✅ Portal validation successful:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Portal validation failed:', error.response || error);
      throw error;
    }
  },
  
  // Get customer info and summary
  getCustomerInfo: async (customerPublicId) => {
    const response = await apiClient.get(`/portal/customer/${customerPublicId}`);
    return response.data;
  },
  
  // Get customer invoices with filters
  getInvoices: async (customerPublicId, params = {}) => {
    const response = await apiClient.get(`/portal/customer/${customerPublicId}/invoices`, { params });
    return response.data;
  },
  
  // Get specific invoice details
  getInvoice: async (customerPublicId, invoiceNumber, { shortCode, token } = {}) => {
    const headers = {};
    if (shortCode) {
      headers.Authorization = `PortalShortCode ${shortCode}`;
    } else if (token) {
      headers.Authorization = `PortalToken ${token}`;
    }

    const response = await apiClient.get(
      `/portal/customer/${customerPublicId}/invoice/${invoiceNumber}`,
      { headers }
    );
    return response.data;
  },
  
  // Get payment history
  getPaymentHistory: async (customerPublicId, params = {}) => {
    const response = await apiClient.get(`/portal/customer/${customerPublicId}/payment-history`, { params });
    return response.data;
  },

  // Get payment history with refunds
  getPaymentHistoryWithRefunds: async (customerPublicId, params = {}) => {
    const response = await apiClient.get(`/portal/customer/${customerPublicId}/payment-history`, { 
      params: { ...params, includeRefunds: 'true' }
    });
    return response.data;
  },

  // Get refunds for specific invoice
  getInvoiceRefunds: async (customerPublicId, invoiceNumber) => {
    const response = await apiClient.get(`/portal/customer/${customerPublicId}/invoice/${invoiceNumber}/refunds`);
    return response.data;
  },
  
  // Get filtered balances
  getBalances: async (customerPublicId, period = 'this_month', customDates = {}) => {
    const params = { period };
    if (period === 'custom' && customDates.startDate && customDates.endDate) {
      params.startDate = customDates.startDate;
      params.endDate = customDates.endDate;
    }
    const response = await apiClient.get(`/portal/customer/${customerPublicId}/balances`, { params });
    return response.data;
  },
  
  // Create payment session
  createPaymentSession: async (customerPublicId, invoiceIds) => {
    const response = await apiClient.post(`/portal/customer/${customerPublicId}/payment-session`, {
      invoiceIds
    });
    return response.data;
  },
  
  // Vault a card and check surcharge eligibility (Phase 1 of two-phase payment)
  // Returns { storedCard, surcharge } — surcharge is null if not applicable
  // Amount is computed server-side from invoiceIds — never sent from client (fraud prevention)
  vaultAndCheckSurcharge: async (customerPublicId, { hostedCardId, invoiceIds, customerData }) => {
    const response = await apiClient.post(
      `/portal/customer/${customerPublicId}/vault-and-check-surcharge`,
      { hostedCardId, invoiceIds, customerData }
    );
    return response.data;
  },

  // Process payment (Phase 2 — includes surchargeAdviceId when surcharge applies)
  processPayment: async (customerPublicId, paymentData, { idempotencyKey } = {}) => {
    const headers = idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {};
    const response = await apiClient.post(`/portal/customer/${customerPublicId}/process-payment`, paymentData, { headers });
    return response.data;
  },
  
  // Download invoice
  downloadInvoice: async (customerPublicId, invoiceId) => {
    const response = await apiClient.get(`/portal/customer/${customerPublicId}/download/${invoiceId}`, {
      responseType: 'blob'
    });
    return response.data;
  },

  // Get estimate details (portal view)
  getEstimate: async (customerPublicId, estimateNumber) => {
    const response = await apiClient.get(`/portal/customer/${customerPublicId}/estimate/${estimateNumber}`);
    return response.data;
  },

  // Accept estimate
  acceptEstimate: async (customerPublicId, estimateNumber) => {
    const response = await apiClient.post(`/portal/customer/${customerPublicId}/estimate/${estimateNumber}/accept`);
    return response.data;
  },

  // Decline estimate (with optional reason)
  declineEstimate: async (customerPublicId, estimateNumber, reason = null) => {
    const response = await apiClient.post(`/portal/customer/${customerPublicId}/estimate/${estimateNumber}/decline`, {
      reason
    });
    return response.data;
  },

  // Accept and convert estimate to invoice (atomic)
  acceptAndConvertEstimate: async (customerPublicId, estimateNumber) => {
    const response = await apiClient.post(`/portal/customer/${customerPublicId}/estimate/${estimateNumber}/accept-and-convert`);
    return response.data;
  },

  // Download estimate PDF
  downloadEstimatePdf: async (customerPublicId, estimateNumber) => {
    const response = await apiClient.get(`/portal/customer/${customerPublicId}/estimate/${estimateNumber}/download`, {
      responseType: 'blob'
    });
    return response.data;
  },

  // Check if payment processing is configured for the customer's company
  getPaymentStatus: async (customerPublicId) => {
    const response = await apiClient.get(`/portal/customer/${customerPublicId}/payment-status`);
    return response.data;
  }
};

export default portalService;
